import { decodeMultimodalQuery } from "@faire/deprecated-retailer-visitor-shared/features/MultimodalProductSearch/multimodalQuery";
import { getSettingShouldDisplayRetrievalAndRankingDebugInfo } from "@faire/deprecated-retailer-visitor-shared/settings/getSettingShouldDisplayRetrievalAndRankingDebugInfo";
import { returnRetrievalRankingDebugInfo } from "@faire/retailer-shared/search/loggedInBrandLeanProductTilesRequest";
import { path as getPathPublishedBrandProductCollectionMetadata } from "@faire/web-api--source/endpoints/www/api/brand/brandToken/published-product-collection-metadata/get";
import fetchCategoryPageLayoutElements from "@faire/web-api--source/endpoints/www/api/layout/category-page/post";
import brandSearch, {
  QueryParameters as BrandSearchParameters,
} from "@faire/web-api--source/endpoints/www/api/v2/search/brands/get";
import getFeaturedProducts, {
  QueryParameters as FeaturedProductsParameters,
} from "@faire/web-api--source/endpoints/www/api/v2/search/brandToken/featured-products/get";
import fetchProductCatNav from "@faire/web-api--source/endpoints/www/api/v2/search/products/cat-nav/post";
import { path as getPathProductsFromBrandFilters } from "@faire/web-api--source/endpoints/www/api/v2/search/products/from-brand/filters/post";
import searchProductsFromBrand, {
  path as getPathProductsFromBrand,
} from "@faire/web-api--source/endpoints/www/api/v2/search/products/from-brand/post";
import queryRelatedContentService from "@faire/web-api--source/endpoints/www/api/v3/layout/logged-out/related-search-content/post";
import queryProductsLayoutService from "@faire/web-api--source/endpoints/www/api/v3/layout/search-product-tiles/post";
import queryProductsMultimodalSearchServiceMultimodal from "@faire/web-api--source/endpoints/www/api/v3/search/multimodal/products/post";
import queryProductsFilterService from "@faire/web-api--source/endpoints/www/api/v3/search/products/filters/post";
import queryProductsSearchService from "@faire/web-api--source/endpoints/www/api/v3/search/products/post";
import { IGetLoggedOutRelatedSearchContentRequest } from "@faire/web-api--source/indigofair/data/IGetLoggedOutRelatedSearchContentRequest";
import { IGetProductFilterSectionInfoResponse } from "@faire/web-api--source/indigofair/data/IGetProductFilterSectionInfoResponse";
import { IGetSearchPageProductTilesRequest } from "@faire/web-api--source/indigofair/data/IGetSearchPageProductTilesRequest";
import { IGetSearchProductsInfoResponse } from "@faire/web-api--source/indigofair/data/IGetSearchProductsInfoResponse";
import { IProductFirstCatNavRequest } from "@faire/web-api--source/indigofair/data/IProductFirstCatNavRequest";
import { IProductFirstCatNavWithLeanProductTileResponse } from "@faire/web-api--source/indigofair/data/IProductFirstCatNavWithLeanProductTileResponse";
import { IQueryProductsRequest } from "@faire/web-api--source/indigofair/data/IQueryProductsRequest";
import { IRequestPaginationData } from "@faire/web-api--source/indigofair/data/IRequestPaginationData";
import { ISearchBrandsV2Response } from "@faire/web-api--source/indigofair/data/ISearchBrandsV2Response";
import { ISearchProductsFromBrandRequest } from "@faire/web-api--source/indigofair/data/ISearchProductsFromBrandRequest";
import { IUser } from "@faire/web-api--source/indigofair/data/IUser";
import { EndpointOptions } from "@faire/web-api--source/types";
import { cachingApi, cachingApiWithLookup } from "@faire/web/common/cachingApi";
import { createStoreHookNonReactive } from "@faire/web/common/hooks/useStoreNonReactive";
import { singletonGetter } from "@faire/web/common/singletons/getter";
import type { QueryClient } from "@tanstack/react-query";

const queryProductsWrapper = (
  data: IQueryProductsRequest,
  abortSignal?: AbortSignal
): Promise<IGetSearchProductsInfoResponse> => {
  const multimodalQuery = decodeMultimodalQuery(data.query);
  if (multimodalQuery) {
    data.query = multimodalQuery.text;
    return queryProductsMultimodalSearchServiceMultimodal(
      {
        query_products_request: data,
        image_urls: multimodalQuery.imageUrls,
      },
      { signal: abortSignal }
    );
  }

  return queryProductsSearchService(data, { signal: abortSignal });
};

const queryProductsFromLayoutWrapper = (
  data: IQueryProductsRequest,
  abortSignal?: AbortSignal
): Promise<IGetSearchProductsInfoResponse> => {
  // Convert IQueryProductsRequest to IGetSearchPageProductTilesRequest
  const layoutRequest: IGetSearchPageProductTilesRequest = {
    query: data.query,
    filter_keys: data.filter_keys,
    pagination_data: IRequestPaginationData.build({
      page_number: (data.page_number ?? 0) + 1,
      page_size: data.page_size,
    }),
    container_name: data.container_name,
  };
  const relatedContentRequest: IGetLoggedOutRelatedSearchContentRequest = {
    query: data.query,
    filter_keys: data.filter_keys,
    pagination_data: IRequestPaginationData.build({
      page_number: (data.page_number ?? 0) + 1,
      page_size: data.page_size,
    }),
    container_name: data.container_name,
  };

  // Call both the product tiles and related content endpoints in parallel
  return Promise.all([
    queryProductsLayoutService(layoutRequest, { signal: abortSignal }),
    queryRelatedContentService(relatedContentRequest, { signal: abortSignal }),
  ]).then(([productTilesResponse, relatedContentResponse]) => {
    // Convert IGetSearchPageProductTilesResponse to IGetSearchProductsInfoResponse
    return IGetSearchProductsInfoResponse.build({
      product_tiles: productTilesResponse.product_tiles,
      brands_by_token: productTilesResponse.brands_by_token,
      pagination_data: productTilesResponse.pagination_data,
      request_id: productTilesResponse.request_id,
      should_show_vertical_imagery:
        productTilesResponse.should_show_vertical_imagery,
      related_search_terms_pills:
        relatedContentResponse.related_search_terms_pills,
      related_search_terms:
        relatedContentResponse.related_search_terms_pills.map(
          (pill) => pill.name ?? ""
        ),
      more_in_c3: relatedContentResponse.more_in_c3,
    });
  });
};

const queryFiltersWrapper = (
  data: IQueryProductsRequest,
  abortSignal?: AbortSignal
): Promise<IGetProductFilterSectionInfoResponse> => {
  return queryProductsFilterService(data, { signal: abortSignal });
};

const fetchProductCatNavWrapper = (
  data: IProductFirstCatNavRequest,
  user: IUser | undefined,
  requestOptions?: EndpointOptions
): Promise<IProductFirstCatNavWithLeanProductTileResponse> => {
  data.return_retrieval_and_ranking_debug_info =
    returnRetrievalRankingDebugInfo(
      user,
      getSettingShouldDisplayRetrievalAndRankingDebugInfo()
    );
  // New search service API that searches the products.
  return fetchProductCatNav(data, requestOptions);
};

// need this for tests
const brandSearchWrapper = (
  payload: BrandSearchParameters,
  requestOptions?: EndpointOptions
): Promise<ISearchBrandsV2Response> => brandSearch(payload, requestOptions);

export class ProductCacheStore {
  /**
   * @trackfunction
   */
  static get = singletonGetter(ProductCacheStore);

  fetchProductCatNav = cachingApi(fetchProductCatNavWrapper);

  fetchCategoryPageLayoutElements = cachingApi(fetchCategoryPageLayoutElements);

  // searchProductsFromBrandCache needs wrapper to enable manual cache invalidation
  // when a product's state is modified
  private searchProductsFromBrandCache = cachingApiWithLookup(
    (request: ISearchProductsFromBrandRequest) => {
      if (!request.brand_token) {
        return Promise.reject("Empty brand_token");
      }
      return searchProductsFromBrand(request);
    }
  );

  private queryProductsMemoized = cachingApi(queryProductsWrapper);

  private queryProductsFromLayoutMemoized = cachingApi(
    queryProductsFromLayoutWrapper
  );

  private queryFiltersMemoized = cachingApi(queryFiltersWrapper);

  private brandSearchMemoized = cachingApi(brandSearchWrapper);

  private getFeaturedLeanProductTilesMemoized = cachingApi(getFeaturedProducts);

  searchProductsFromBrand = (payload: ISearchProductsFromBrandRequest) => {
    this.searchProductsFromBrandCache.lookup.add(payload);
    return this.searchProductsFromBrandCache.cache(payload);
  };

  // Search results view caches

  queryProducts = (
    payload: IQueryProductsRequest,
    abortSignal?: AbortSignal
  ) => {
    return this.queryProductsMemoized(payload, abortSignal);
  };

  queryProductsFromLayout = (
    payload: IQueryProductsRequest,
    abortSignal?: AbortSignal
  ) => {
    return this.queryProductsFromLayoutMemoized(payload, abortSignal);
  };

  queryFilters = (
    payload: IQueryProductsRequest,
    abortSignal?: AbortSignal
  ) => {
    return this.queryFiltersMemoized(payload, abortSignal);
  };

  // Category view results view caches
  brandSearch = (
    payload: BrandSearchParameters,
    requestOptions?: EndpointOptions
  ) => {
    return this.brandSearchMemoized(payload, requestOptions);
  };

  getFeaturedLeanProductTiles = (
    brandToken: string,
    queryParams?: FeaturedProductsParameters
  ) => {
    return this.getFeaturedLeanProductTilesMemoized(brandToken, queryParams);
  };

  // Invalidate a brand's product cache when products of that
  // brand have had their state modified
  invalidateCache = (
    brandToken: string | undefined,
    queryClient: QueryClient | null
  ) => {
    if (brandToken === undefined) {
      return;
    }

    // Invalidate the entire cache of search calls if any product is favorited
    this.queryProductsMemoized.clear();
    this.queryProductsFromLayoutMemoized.clear();
    this.queryFiltersMemoized.clear();
    this.brandSearchMemoized.clear();
    this.getFeaturedLeanProductTilesMemoized.clear();

    this.searchProductsFromBrandCache.lookup.forEach((payload) => {
      if (payload.brand_token === brandToken) {
        this.searchProductsFromBrandCache.cache.delete(payload);
      }
    });

    if (queryClient) {
      queryClient.removeQueries({
        queryKey: [getPathPublishedBrandProductCollectionMetadata(brandToken)],
      });
      queryClient.removeQueries({
        queryKey: [getPathProductsFromBrand(), { brand_token: brandToken }],
      });
      queryClient.removeQueries({
        queryKey: [
          getPathProductsFromBrandFilters(),
          { brand_token: brandToken },
        ],
      });
    }
  };

  invalidateCacheAfterPlacingAndCancelingOrders = () => {
    this.brandSearchMemoized.clear();
    this.queryProductsMemoized.clear();
    this.queryFiltersMemoized.clear();
  };

  invalidateCacheAfterInsiderEnrollment = () => {
    this.brandSearchMemoized.clear();
  };
}

export const useProductCacheStore =
  createStoreHookNonReactive(ProductCacheStore);
